import cn from 'classnames';
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form, Modal } from 'react-bootstrap';
import { Checkbox } from '@Components/FormCheck';
import * as uuid from 'uuid';
import { S3Put } from '../../utils/fetch-utils';
import { parseWholeUsdOrNull } from '../../utils/text-utils';
import { useCurrencyInputDisplay } from '../../utils/hooks/useCurrencyInputDisplay';
import { BPMFileInput } from '../BPMFileInput/BPMFileInput';
import { Button } from '../Button/Button';
import { DatePicker } from '../DatePicker';
import { Appearance, Design, Intent, Size } from '../designConstants';
import { InfoTooltip } from '../InfoTooltip/InfoTooltip';
import {
  CheckCanDeleteFn,
  CheckIsValidFn,
  CustomFooterButton,
  CustomModalEditWidgetHeader,
  FileToUpload,
  SelectorOptions,
  ValidationResult
} from './modal-edit-constants';
import { SelectorElement } from './SelectorElement';

interface ModalEditorProps<T> {
  name: string | ReactNode;
  modalTitle: string | ReactNode;
  headers: CustomModalEditWidgetHeader[];
  checkIsValid: CheckIsValidFn<T>;
  checkCanDelete?: CheckCanDeleteFn<T>;
  enableDelete: boolean;
  onHide: () => void;
  onSaveModal: (data: T) => void;
  onDelete?: () => void;
  isAddingNewRow: boolean;
  // An added-but-unsaved row reopened from the table; shows Delete.
  isPendingAdd?: boolean;
  rowData: Partial<T>;
  selectorOptions: SelectorOptions;
  footerText?: string;
  footerMetadata?: (data: Partial<T>) => ReactNode;
  deleteButtonText?: string;
  saveButtonText?: string;
  customFooterButtons?: CustomFooterButton<T>[];
  checkShowFooter?: (data: Partial<T>) => boolean;
  fileNamePrefix?: string;
}

export const ModalEditor = <T extends object>({
  name,
  modalTitle,
  headers,
  checkIsValid,
  checkCanDelete,
  enableDelete,
  onHide,
  onSaveModal,
  onDelete,
  isAddingNewRow,
  isPendingAdd = false,
  rowData,
  selectorOptions,
  footerText,
  footerMetadata,
  deleteButtonText = 'Delete',
  saveButtonText = 'Save',
  customFooterButtons,
  checkShowFooter,
  fileNamePrefix = ''
}: ModalEditorProps<T>): ReactNode => {
  const [editedData, setEditedData] = useState({} as Partial<T>);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const data: Partial<T> = useMemo(() => ({ ...rowData, ...editedData }), [editedData, rowData]);
  const [pdfToUpload, setPdfToUpload] = useState<FileToUpload>();
  // Select menus portal here (see SelectorElement's menuPortalTarget).
  const [modalBody, setModalBody] = useState<HTMLDivElement | null>(null);

  const [validationResult, setValidationResult] = useState<ValidationResult>({
    isValid: true,
    errorMessage: ''
  });
  const [canDeleteRow, setCanDeleteRow] = useState(true);

  // Run validation and update result when data changes
  useEffect(() => {
    if (!checkIsValid) return () => {};

    let isMounted = true;

    const validateData = async () => {
      let errorMessage = '';
      const fullData = data as T;
      const isValid = await checkIsValid(fullData, msg => {
        errorMessage = msg;
      });
      if (isMounted) {
        setValidationResult({ isValid, errorMessage });
      }
    };

    validateData();

    return () => {
      isMounted = false;
    };
  }, [data, checkIsValid]);

  // Opt-in, separate from checkIsValid: a row can be undeletable for a reason that has nothing
  // to do with whether its (possibly in-progress, unsaved) field values pass validation -- e.g.
  // QueryExporter blocks deleting a row that's still an unconfirmed insert. Defaults to always
  // allowed so existing consumers that don't pass checkCanDelete see no behavior change.
  useEffect(() => {
    if (!checkCanDelete) return () => {};

    let isMounted = true;

    Promise.resolve(checkCanDelete(rowData)).then(canDelete => {
      if (isMounted) {
        setCanDeleteRow(canDelete);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [rowData, checkCanDelete]);

  const isRowValid = !checkIsValid || validationResult.isValid;
  const shouldShowFooterText = footerText && (checkShowFooter ? checkShowFooter(rowData) : true);

  const onSave = useCallback(async () => {
    if (!isAddingNewRow && Object.keys(editedData).length === 0) {
      onHide();
      return;
    }

    let isValid = true;

    if (checkIsValid) {
      let errorMessage = '';
      isValid = await checkIsValid(data, msg => {
        errorMessage = msg;
      });
      setValidationResult({ isValid, errorMessage });
    }

    if (!isValid) {
      setSaveAttempted(true);
      return;
    }

    if (pdfToUpload) {
      const reader = new FileReader();
      reader.onload = e => {
        const result = e?.target?.result;
        if (typeof result !== 'string') return;
        const fileContent = result.split(',')[1];
        S3Put('bpm-cdn', pdfToUpload.fileName, fileContent, {
          bufferEncoding: 'base64',
          contentType: 'application/pdf'
        });
      };
      reader.readAsDataURL(pdfToUpload.file);
    }

    // Assume that, if passed validation, all the required fields are present
    onSaveModal(data as T);
    onHide();
  }, [checkIsValid, data, editedData, isAddingNewRow, pdfToUpload, onSaveModal, onHide]);

  // Fields currently shown in the editor (hideInModal may depend on the row being edited).
  const visibleHeaders = useMemo(
    () =>
      headers.filter(header =>
        typeof header.hideInModal === 'function' ? !header.hideInModal(data) : !header.hideInModal
      ),
    [data, headers]
  );

  // Only visible fields can block Save.
  const invalidSet = useMemo(
    () =>
      visibleHeaders.reduce<Record<string, boolean>>((acc, header) => {
        if (header.isInvalid && header.isInvalid(data[header.field ?? ''])) {
          acc[header.field ?? ''] = true;
        }
        return acc;
      }, {}),
    [data, visibleHeaders]
  );

  // Row-level errors show after the first Save attempt, then update live. Field-level isInvalid
  // errors disable Save, so they show right away.
  const showValidationError = !isRowValid && (saveAttempted || Object.keys(invalidSet).length > 0);

  const makeInputElement = useCallback(
    (header: CustomModalEditWidgetHeader) => {
      const field = header.field ?? '';
      const headerType = header.rendererType ? header.rendererType(data) : header.type;
      const emptyNumber = header.omitEmptyNumber ? undefined : null;
      if (header.uneditable) {
        return undefined;
      }
      switch (headerType) {
        case 'select':
          return (
            <SelectorElement
              header={header}
              value={data[field] ?? header.default}
              row={data}
              key={field}
              isValidNewOption={header.isValidNewOption}
              menuPortalTarget={modalBody}
              setValue={newValue => {
                setEditedData({ ...editedData, [field]: newValue });
              }}
              selectorOptions={selectorOptions}
            />
          );
        case 'currency':
          return (
            <CurrencyField
              key={field}
              dataTestId={`field-${field}`}
              isInvalid={invalidSet[field]}
              value={data[field] ?? null}
              onChange={newValue =>
                setEditedData({ ...editedData, [field]: newValue ?? emptyNumber })
              }
            />
          );
        case 'percent':
          return (
            <Form.Control
              size='lg'
              isInvalid={invalidSet[field]}
              value={
                data[field] != null
                  ? parseFloat(data[field]).toLocaleString('en-US', {
                      style: 'percent',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    })
                  : ''
              }
              onChange={event => {
                let newVal: number = parseFloat(event.target.value.replace(/[^0-9]/g, ''));
                if (!Number.isNaN(newVal)) {
                  newVal /= 100;
                  newVal = Math.min(1, Math.max(0, newVal));
                }
                setEditedData({
                  ...editedData,
                  [field]: Number.isNaN(newVal) ? emptyNumber : newVal
                });
              }}
            />
          );
        case 'real':
          return (
            <Form.Control
              size='lg'
              isInvalid={invalidSet[field]}
              value={
                data[field] || data[field] === 0
                  ? parseFloat(data[field]).toLocaleString('en-US', {
                      style: 'decimal',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })
                  : ''
              }
              onChange={event => {
                const newVal = parseFloat(event.target.value.replace(/[^0-9\\.]/g, ''));
                setEditedData({
                  ...editedData,
                  [field]: Number.isNaN(newVal) ? emptyNumber : newVal
                });
              }}
            />
          );
        case 'integer':
          return (
            <Form.Control
              size='lg'
              isInvalid={invalidSet[field]}
              value={data[field] || data[field] === 0 ? parseFloat(data[field]) : ''}
              onChange={event => {
                const newVal = parseInt(event.target.value.replace(/[^0-9\\.]/g, ''), 10);
                setEditedData({
                  ...editedData,
                  [field]: Number.isNaN(newVal) ? emptyNumber : newVal
                });
              }}
            />
          );
        case 'decimal':
          return (
            <Form.Control
              size='lg'
              type='number'
              isInvalid={invalidSet[field]}
              value={data[field] || data[field] === 0 ? parseFloat(data[field]) : ''}
              min={header.min}
              max={header.max}
              step={header.step}
              onChange={event => {
                let newVal = parseFloat(event.target.value);
                if (!Number.isNaN(newVal) && newVal < 0) {
                  newVal = 0;
                }
                setEditedData({
                  ...editedData,
                  [field]: Number.isNaN(newVal) ? emptyNumber : newVal
                });
              }}
            />
          );
        case 'week':
          return (
            <DatePicker
              isSingle
              range={{ start: data[field] || '', end: data[field] || '' }}
              isOutsideRange={() => false}
              isDayBlocked={date => new Date(`${date}T00:00:00`).getDay() !== 1}
              onChange={newDates => {
                if (newDates.start) {
                  setEditedData({ ...editedData, [field]: newDates.start });
                }
              }}
            />
          );
        case 'day':
          return (
            <DatePicker
              isSingle
              isClearable
              range={{ start: data[field] || '', end: data[field] || '' }}
              isOutsideRange={() => false}
              isDayBlocked={header.restriction}
              onChange={newDates => {
                setEditedData({ ...editedData, [field]: newDates.start });
              }}
            />
          );
        case 'time':
          return (
            <Form.Control
              size='lg'
              isInvalid={invalidSet[field]}
              type='time'
              value={data[field] ?? ''}
              key={field}
              readOnly={header.readOnly}
              onChange={e => setEditedData({ ...editedData, [field]: e.target.value || null })}
            />
          );
        case 'checkbox':
          return (
            <Checkbox
              checked={data[field]}
              onChange={e => {
                setEditedData({ ...editedData, [field]: e.target.checked });
              }}
            />
          );
        case 'file':
          return (
            <BPMFileInput
              buttonText='Upload PDF'
              id='fileInput'
              name='fileInput'
              onChange={e => {
                const rowDataWithPdf = rowData as T & { pdf?: string };
                const uploadedFile = e.target.files;
                const file = uploadedFile?.[0];
                const fileName = rowDataWithPdf?.pdf || `${fileNamePrefix}/${uuid.v4()}.pdf`;

                setEditedData({ ...editedData, [field]: fileName });
                setPdfToUpload({ fileName, file });
              }}
            />
          );
        case 'query':
          return (
            <Form.Control
              className='modal-edit-text-area'
              isInvalid={invalidSet[field]}
              as='textarea'
              rows={header.rows ?? 4}
              defaultValue={data[field]}
              key={field}
              readOnly={header.readOnly}
              onChange={e =>
                setEditedData({
                  ...editedData,
                  [field]: e.target.value ? e.target.value : null
                })
              }
            />
          );
        case 'richText':
          return (
            <Form.Control
              className='modal-edit-text-area'
              isInvalid={invalidSet[field]}
              as='textarea'
              rows={header.rows ?? 4}
              defaultValue={data[field] ?? ''}
              readOnly={header.readOnly}
              onChange={e =>
                setEditedData({
                  ...editedData,
                  [field]: e.target.value
                })
              }
            />
          );
        case 'cron':
          return (
            <>
              <Form.Control
                size='lg'
                type='text'
                isInvalid={invalidSet[field]}
                value={data[field] ?? ''}
                key={field}
                readOnly={header.readOnly}
                data-testid={`field-${field}`}
                onChange={e =>
                  setEditedData({
                    ...editedData,
                    [field]: e.target.value ? e.target.value : null
                  })
                }
              />
              {invalidSet[field] && (
                <Form.Control.Feedback type='invalid'>
                  Invalid cron expression
                </Form.Control.Feedback>
              )}
            </>
          );
        case 'custom': {
          const handleChange = (newData: any) => {
            setEditedData({ ...newData });
          };
          return header.customRenderer?.(data, handleChange);
        }
        default:
          return (
            <Form.Control
              size='lg'
              isInvalid={invalidSet[field]}
              type='text'
              defaultValue={data[field]}
              key={field}
              readOnly={header.readOnly}
              data-testid={`field-${field}`}
              onChange={e =>
                setEditedData({
                  ...editedData,
                  [field]: e.target.value ? e.target.value.replace(/,/g, '').trim() : null
                })
              }
            />
          );
      }
    },
    [data, editedData, fileNamePrefix, invalidSet, modalBody, rowData, selectorOptions]
  );

  return (
    <Modal
      size='lg'
      keyboard
      show
      onHide={onHide}
      className='modal-widget-editor'
      data-testid='edit-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle || name}</Modal.Title>
      </Modal.Header>
      <Modal.Body ref={setModalBody}>
        {Object.values(
          visibleHeaders.reduce<Record<string, CustomModalEditWidgetHeader[]>>((acc, elem) => {
            const key = String(elem.modalRow ?? '');
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(elem);
            return acc;
          }, {})
        )
          .sort((a, b) => ((a[0]?.modalRow ?? 0) > (b[0]?.modalRow ?? 0) ? 1 : -1))
          .map(row => (
            <Fragment key={String(row[0].modalRow)}>
              <RowNote note={row.find(header => header.modalNote)?.modalNote} />
              <div className='modal-row'>
                {row.map(header =>
                  header.uneditable ? (
                    <div key={header.field} />
                  ) : (
                    <Form.Group
                      key={header.field}
                      style={{
                        flex: header.modalFlex || 0,
                        minWidth: header.modalWidth || 0
                      }}
                    >
                      {header.type !== '' && (
                        <Form.Label className={cn('modal-header-label')}>
                          {header.modalLabel ?? header.label}
                          {header.required && <span className='required-marker'>*</span>}
                          {/* Optional info tooltip that takes in an array of strings
                          to render as paragraphs within the tooltip. */}
                          {header.infoTextParagraphs && (
                            <InfoTooltip size={Size.SM}>
                              {header.infoTextParagraphs.map(paragraph => (
                                <div className='info-text-paragraph' key={paragraph}>
                                  {paragraph}
                                </div>
                              ))}
                            </InfoTooltip>
                          )}
                        </Form.Label>
                      )}
                      {makeInputElement(header)}
                    </Form.Group>
                  )
                )}
              </div>
            </Fragment>
          ))}
        {shouldShowFooterText && (
          <div className='alert-container'>
            <Alert className='invalid-text'>{footerText}</Alert>
          </div>
        )}
        {showValidationError && (
          <div className='alert-container'>
            <Alert className='invalid-text' variant='danger' data-testid='validation-error'>
              {validationResult.errorMessage}
            </Alert>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className='modal-controls'>
        <div className='modal-controls-left'>
          {footerMetadata && <div className='modal-footer-metadata'>{footerMetadata(data)}</div>}
          {enableDelete && onDelete && (!isAddingNewRow || isPendingAdd) && canDeleteRow && (
            <Button
              design={Intent.ERROR}
              className='delete'
              onClick={() => {
                onDelete();
                onHide();
              }}
            >
              {deleteButtonText}
            </Button>
          )}
        </div>
        <div className='modal-controls-right'>
          {customFooterButtons
            ?.filter(button => {
              switch (button.showWhen) {
                case 'adding':
                  return isAddingNewRow;
                case 'editing':
                  return !isAddingNewRow;
                default:
                  return true;
              }
            })
            .map(button => (
              <Button
                appearance={button.variant ?? Appearance.FILLED}
                onClick={() => button.onClick(rowData as T)}
                key={button.label}
              >
                {button.label}
              </Button>
            ))}
          <Button
            className='cancel-button'
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            onClick={onHide}
          >
            Cancel
          </Button>
          <Button
            className='send-button'
            disabled={Object.keys(invalidSet).length > 0}
            onClick={onSave}
          >
            <span>{saveButtonText}</span>
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

interface CurrencyFieldProps {
  value: number | null;
  isInvalid: boolean;
  dataTestId: string;
  onChange: (value: number | null) => void;
}

// A private component (not inlined in makeInputElement's switch) because useCurrencyInputDisplay
// is a hook -- it needs a stable component instance to call it from, which a plain callback
// invoked per-header can't provide.
const CurrencyField = ({ value, isInvalid, dataTestId, onChange }: CurrencyFieldProps) => {
  const { displayValue, onFocus, onChangeText, onBlur } = useCurrencyInputDisplay(value);

  return (
    <Form.Control
      size='lg'
      isInvalid={isInvalid}
      data-testid={dataTestId}
      value={displayValue}
      onFocus={onFocus}
      onChange={event => {
        onChangeText(event.target.value);
        onChange(parseWholeUsdOrNull(event.target.value));
      }}
      onBlur={onBlur}
    />
  );
};

// Hint line above a modal row -- taken from whichever of the row's fields sets modalNote.
interface RowNoteProps {
  note?: string;
}

const RowNote = ({ note }: RowNoteProps) =>
  note ? <div className='modal-row-note'>{note}</div> : null;
